'use client';

import { Building2 } from 'lucide-react';
import Image from 'next/image';
import { stripMarkdown } from '@/lib/markdown-utils';

export interface OrganizerCardOrganization {
  id?: string;
  name: string;
  description?: string | null;
  logo_url?: string | null;
}

export interface OrganizerCardProps {
  organization: OrganizerCardOrganization;
  /** compact: small logo, description line-clamp-2; default: medium; full: no clamp; inline: table/cell style, description line-clamp-1 */
  variant?: 'compact' | 'default' | 'full' | 'inline';
  /** Optional action slot (e.g. Settings button) - shown on the right on default/full */
  action?: React.ReactNode;
  /** Optional trailing slot (e.g. role badge) - shown after name/description row for compact */
  trailing?: React.ReactNode;
  className?: string;
  /** Optional wrapper - if 'card', wraps in the standard card border/padding; 'none' = just the row */
  wrapper?: 'card' | 'none';
}

const logoSizes = {
  compact: 'h-10 w-10 sm:h-10 sm:w-10',
  default: 'h-14 w-14 sm:h-16 sm:w-16',
  full: 'h-14 w-14 sm:h-16 sm:w-16',
  inline: 'h-10 w-10 sm:h-12 sm:w-12',
} as const;

const descriptionClamp = {
  compact: 'line-clamp-2',
  default: 'line-clamp-2 sm:line-clamp-none',
  full: '',
  inline: 'line-clamp-1',
} as const;

export default function OrganizerCard({
  organization,
  variant = 'default',
  action,
  trailing,
  className = '',
  wrapper = 'card',
}: OrganizerCardProps) {
  const logoSize = logoSizes[variant];
  const clampClass = descriptionClamp[variant];
  const isInline = variant === 'inline';

  const content = (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${isInline ? 'flex-row items-center gap-3' : ''} ${className}`.trim()}>
      <div className={`flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0 ${isInline ? 'items-center gap-3' : ''}`}>
        {organization.logo_url ? (
          <div className={`relative ${logoSize} flex-shrink-0 rounded-md overflow-hidden`}>
            <Image
              src={organization.logo_url}
              alt={organization.name || 'Organization logo'}
              fill
              className="object-cover"
              unoptimized
            />
          </div>
        ) : (
          <div className={`${logoSize} rounded-md bg-blue-600 flex items-center justify-center text-white flex-shrink-0`}>
            <Building2 className={variant === 'compact' || variant === 'inline' ? 'w-5 h-5' : 'w-7 h-7 sm:w-8 sm:h-8'} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h2 className={`font-bold text-slate-900 dark:text-slate-100 break-words ${
            variant === 'compact' ? 'text-sm' : variant === 'inline' ? 'text-sm font-medium truncate' : 'text-base sm:text-lg'
          } ${variant === 'default' || variant === 'full' ? 'line-clamp-2 sm:line-clamp-none' : ''}`}>
            {organization.name}
          </h2>
          {organization.description && (
            <p className={`text-sm text-slate-600 dark:text-slate-400 mt-1 overflow-hidden ${clampClass} whitespace-pre-line`}>
              {stripMarkdown(organization.description)}
            </p>
          )}
        </div>
      </div>
      {trailing && (variant === 'compact' || variant === 'default' || variant === 'inline') && (
        <div className="flex-shrink-0">{trailing}</div>
      )}
      {action && (variant === 'default' || variant === 'full') && (
        <div className="w-full sm:w-auto flex-shrink-0">{action}</div>
      )}
    </div>
  );

  if (wrapper === 'card') {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 sm:p-6">
        {content}
      </div>
    );
  }

  return content;
}
